/**
 * KnowledgeShell — vibe-designer 1.170.1 hotfix (Phase 1+2 layout regression).
 *
 * Mirrors AppShell + TerminalsShell chrome (h-screen flex column with
 * TopBar + VoicePanel at the top) so the Designer/Knowledge/Terminals
 * tab switcher AND the voice strip remain visible on every page. Without
 * this, switching to the Knowledge tab hides the top-nav (TopBar was
 * mounted only inside the Designer/Terminals shells which go
 * display:none under TriShellLayout's contents/none toggle).
 *
 * Body layout: 2-column with sidebar + panel — unchanged from Phase 2.
 * On mount, fetches /api/knowledge/overview via knowledgeStore.
 *
 * Design doc + worldview anchor:
 * agentic_workflows/docs/20260520/20260520_180000_DESIGN_KNOWLEDGE_TAB_TENANT_CORPUS_DASHBOARD.md
 */
import { useEffect, useState } from 'react';
import { ResizableVoicePanel } from '../voice/ResizableVoicePanel';
import { KnowledgeSidebar, type KnowledgeNavKey } from './KnowledgeSidebar';
import { KnowledgeOverviewPanel } from './KnowledgeOverviewPanel';
import { KnowledgeServiceDetail } from './KnowledgeServiceDetail';
import { useKnowledgeStore } from '@/stores/knowledgeStore';
import { useUIStore } from '@/stores/uiStore';
import { useSpaContextStore } from '@/stores/spaContextStore';
import { buildKnowledgeSnapshot } from './knowledgeSnapshot';

const NAV_KEYS: KnowledgeNavKey[] = [
  'overview', 'mind', 'cortex', 'brain', 'sight', 'cognition', 'obsidian', 'web_search',
  'synapse',
];

export function KnowledgeShell() {
  const [selectedNav, setSelectedNav] = useState<KnowledgeNavKey>('overview');
  const fetchOverview = useKnowledgeStore((s) => s.fetchOverview);
  const overview = useKnowledgeStore((s) => s.overview);
  // 2026-07-02 — SYNAPSE loop status (own route; not part of /overview).
  const fetchSynapseStatus = useKnowledgeStore((s) => s.fetchSynapseStatus);
  const synapseStatus = useKnowledgeStore((s) => s.synapseStatus);
  // 1.191.0 Phase 7.6.1 — re-fetch overview when SSE delivers an ingest event.
  const lastIngestAt = useKnowledgeStore((s) => s.lastIngestAt);

  useEffect(() => {
    void fetchOverview();
    void fetchSynapseStatus();
  }, [fetchOverview, fetchSynapseStatus, lastIngestAt]);

  // Publish an on-screen snapshot so the voice ("BRIDGE") and Pi agents can SEE
  // the tenant's knowledge corpus (per-service counts + the open nav). GATED on
  // the Knowledge view being active (all shells stay mounted under display:none;
  // only the visible one owns the shared snapshot). (See ghl/App.tsx.)
  const viewMode = useUIStore((s) => s.viewMode);
  const setSpaContext = useSpaContextStore((s) => s.setSpaContext);
  useEffect(() => {
    if (viewMode !== 'knowledge') return;
    setSpaContext(buildKnowledgeSnapshot(overview, selectedNav, synapseStatus));
  }, [viewMode, overview, selectedNav, synapseStatus, setSpaContext]);

  // INTERACT — benign local UI: let the agents open a knowledge nav pane on
  // command (vibe:module-action set_nav). All ingest/write actions stay in the
  // panels' own confirm-gated flows, never on the bus.
  useEffect(() => {
    const onModuleAction = (e: Event) => {
      const d = (e as CustomEvent).detail as
        | { action?: string; params?: Record<string, unknown> }
        | undefined;
      if (viewMode !== 'knowledge' || d?.action !== 'set_nav') return;
      const nav = d.params?.nav ?? d.params?.service;
      if (typeof nav === 'string' && (NAV_KEYS as string[]).includes(nav)) {
        setSelectedNav(nav as KnowledgeNavKey);
      }
    };
    window.addEventListener('vibe:module-action', onModuleAction);
    return () => window.removeEventListener('vibe:module-action', onModuleAction);
  }, [viewMode]);

  return (
    <div
      data-testid="knowledge-shell-root"
      className="flex flex-col h-full w-full overflow-hidden bg-vibe-bg text-vibe-text"
    >
      {/* TopBar hoisted to App level (2026-07-03) — voice strip stays here. */}
      <ResizableVoicePanel />
      <div className="flex-1 flex overflow-hidden">
        <KnowledgeSidebar selectedNav={selectedNav} onSelect={setSelectedNav} />
        {selectedNav === 'overview' ? (
          <KnowledgeOverviewPanel />
        ) : (
          <KnowledgeServiceDetail
            // R1 C1 (Phase 7.2): widened cast — was `KnowledgeServiceStats['service']`
            // (6 values). Phase 7.2 added `web_search` to KnowledgeNavKey, so the
            // detail prop must accept the full non-overview nav key space.
            service={selectedNav as Exclude<KnowledgeNavKey, 'overview'>}
          />
        )}
      </div>
    </div>
  );
}

export default KnowledgeShell;
